import { injectIntl, intlShape } from "@ai360/core/intl";
import { ReportOptionsComponent, ReportOptionsComponentProps } from ".";
import { DateInput, NoLink, Section, SubSection } from "~/core";
import CroppingSeason from "../components/inputs/cropping-season";
import { useAppSelector } from "~/store/hooks";
import { getReportDataByReportType, getReportTypePages } from "../data/selectors";
import { ReportAPI } from "@ai360/core";
import { model } from "../data";
import React, { useEffect, useRef } from "react";
import { messages } from "~/reports/i18n-messages";
import PropTypes from "prop-types";
import { ReportPageSelection } from "../components/report-page-selection";
import { getSelectedEventGuidSet } from "~/action-panel/components/event-module/components/event-list/selectors";
import { getEventGeneralGuidToEventSummaryMap } from "~/recs-events/events/selectors";
import { selectors as picklistSelectors, picklistNames } from "~/core/picklist";
import { keywords } from "../keywords";
import { getSelectedCustomerGuids } from "~/customer-data/selectors";

function TillageSummary({
    intl: { formatMessage },
    onInputChange,
    initialPreferences,
}: ReportOptionsComponentProps) {
    const [showPageSelection, setShowPageSelection] = React.useState(false);
    const reportData = useAppSelector(getReportDataByReportType(TillageSummary.reportName));
    const disableOption = useRef<boolean>(false);
    const seasonsToDisplay = useRef<string[]>();
    const reportTypePages = useAppSelector((state) => getReportTypePages(state));
    const agEventSummaryMap = useAppSelector(getEventGeneralGuidToEventSummaryMap);
    const selectedEventGuidList = useAppSelector(getSelectedEventGuidSet);
    const selectedCustomerGuids = useAppSelector(getSelectedCustomerGuids);

    const seasons = useAppSelector((state) =>
        picklistSelectors.getPicklistOptionsFromCode(
            state,
            picklistNames.getPickListCode(picklistNames.PICKLIST_CROPPING_SEASON)
        )
    );

    const reportPageSelection = reportData[model.PROPS_REPORT_TYPE_PAGE_SELECTION]
        ? JSON.parse(reportData[model.PROPS_REPORT_TYPE_PAGE_SELECTION])
        : {};
    const reportPageTypePrefs = initialPreferences[model.PROPS_REPORT_TYPE_PAGE_SELECTION]
        ? JSON.parse(initialPreferences[model.PROPS_REPORT_TYPE_PAGE_SELECTION])
        : {};

    const croppingSeasonDisabled =
        reportData[model.PROPS_START_DATE] ||
        reportData[model.PROPS_END_DATE] ||
        disableOption.current;

    useEffect(() => {
        onInputChange(model.PROPS_CUSTOMER_GUIDS, Array.from(selectedCustomerGuids));
    }, [selectedCustomerGuids.length]);

    useEffect(() => {
        onInputChange(model.PROPS_SELECTED_EVENT_GUID_LIST, [...selectedEventGuidList]);

        if (selectedEventGuidList.size > 0) {
            const seasonGuids = [];

            for (const eventGeneralGuid of selectedEventGuidList) {
                const event = agEventSummaryMap.get(eventGeneralGuid);
                if (event.agEventTypeName === keywords.tillageEvent) {
                    seasonGuids.push(event.croppingSeasonGuid);
                }
            }

            const seasonsText = [];
            for (const seasonGuid of seasonGuids) {
                const selectedSeason = seasons.find((season) => season.value === seasonGuid);
                if (seasonsText.indexOf(selectedSeason.label) === -1) {
                    seasonsText.push(selectedSeason.label);
                }
            }

            seasonsText.sort();
            seasonsToDisplay.current = seasonsText;
            disableOption.current =
                selectedEventGuidList.size > 0
                    ? disableReportOptionsOnSelectedEvent(selectedEventGuidList)
                    : false;
        } else {
            seasonsToDisplay.current = [];
            disableOption.current = false;
        }
    }, [selectedEventGuidList.size]);

    const disableReportOptionsOnSelectedEvent = (selectedEventGuidList: any): boolean => {
        for (const eventGeneralGuid of selectedEventGuidList) {
            const event = agEventSummaryMap.get(eventGeneralGuid);
            if (event.agEventTypeName === keywords.tillageEvent) {
                return true;
            }
        }
    };

    return (
        <div className="report-options">
            <Section testId="tillage-summary-options-section">
                <SubSection testId="tillage-summary-season-subsection">
                    <CroppingSeason
                        disabled={croppingSeasonDisabled}
                        required={!croppingSeasonDisabled}
                        reportType={TillageSummary.reportName}
                        valuesToDisplay={seasonsToDisplay.current}
                    />
                </SubSection>
                <SubSection testId="tillage-summary-dates-subsection">
                    <DateInput
                        placeholderText={formatMessage(messages.startDate)}
                        onChange={(value) => onInputChange(model.PROPS_START_DATE, value)}
                        timeFormat={false}
                        value={reportData[model.PROPS_START_DATE]}
                        disabled={disableOption.current}
                        testId="tillage-summary-start-date-date"
                    />
                    <DateInput
                        placeholderText={formatMessage(messages.endDate)}
                        onChange={(value) => onInputChange(model.PROPS_END_DATE, value)}
                        timeFormat={false}
                        value={reportData[model.PROPS_END_DATE]}
                        disabled={disableOption.current}
                        testId="tillage-summary-end-date-date"
                    />
                </SubSection>
                <SubSection testId="tillage-summary-page-selection-subsection">
                    <NoLink
                        className="categories-link report-options-modal-link page-selection"
                        label={formatMessage(messages.pageSelection)}
                        onClick={() => setShowPageSelection(true)}
                    />
                    <ReportPageSelection
                        isOpen={showPageSelection}
                        onChange={(value) => {
                            onInputChange(
                                model.PROPS_REPORT_TYPE_PAGE_SELECTION,
                                JSON.stringify(value)
                            );
                            setShowPageSelection(false);
                        }}
                        onClose={() => setShowPageSelection(false)}
                        title={formatMessage(messages.pageSelection)}
                        reportTypePages={reportTypePages}
                        reportTypePagesPrefs={reportPageTypePrefs}
                        selectedReportTypes={[TillageSummary.reportName]}
                        selectedReportTypePages={reportPageSelection}
                    />
                </SubSection>
            </Section>
        </div>
    );
}

TillageSummary.propTypes = {
    intl: intlShape,
    reportPreferences: PropTypes.object,
};

TillageSummary.defaultReportOptions = {
    [model.PROPS_END_DATE]: null,
    [model.PROPS_START_DATE]: null,
    [model.PROPS_SELECTED_EVENT_GUID_LIST]: [],
    [model.PROPS_CROPPING_SEASON_GUID_LIST]: [],
    [model.PROPS_REPORT_TYPE_PAGE_SELECTION]: null,
};

TillageSummary.reportPreferences = [model.PROPS_REPORT_TYPE_PAGE_SELECTION];

TillageSummary.reportName = ReportAPI.ReportNames.TILLAGE_SUMMARY;

export default injectIntl(TillageSummary) as ReportOptionsComponent<ReportOptionsComponentProps>;
